import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, ShieldQuestion } from 'lucide-react';
import { apiGet, apiPost } from '@/lib/api';
import { fmtTime } from '@/lib/format';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Drawer } from '@/components/Drawer';
import { DeviceRemoval } from '@/components/DeviceRemoval';
import type { PendingDevice, Site } from '@/types';

/**
 * 待認領裝置。
 *
 * 裝置燒好韌體連上 MQTT 後會自動註冊，但在認領之前平台不收它的事件
 * 與媒體 —— 認領是人為動作，確保任何人撿到韌體燒一片都無法直接把
 * 資料灌進平台。這個區塊只在真的有待認領裝置時出現，平常不佔版面。
 *
 * 解除配對的裝置也回到這裡，可能還留著事件與檔案，所以多一個「刪除」入口；
 * 背景刪除進行中時不能重新認領。
 */
export function PendingDevices() {
  const qc = useQueryClient();
  const [target, setTarget] = useState<PendingDevice | null>(null);
  const [removeKey, setRemoveKey] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['devices-pending'],
    queryFn: () => apiGet<PendingDevice[]>('/api/v1/devices/pending'),
    refetchInterval: 10_000,
  });

  const reject = useMutation({
    mutationFn: (id: string) =>
      apiPost(`/api/v1/devices/${encodeURIComponent(id)}/reject`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['devices-pending'] });
      void qc.invalidateQueries({ queryKey: ['devices'] });
    },
  });

  const rows = data ?? [];

  // 沒有待認領裝置時整個區塊不出現，避免常態版面被空卡片佔據
  if (isLoading || rows.length === 0) return null;

  return (
    <>
      <Card className="overflow-hidden border-warn/40">
        <CardHeader className="pb-5">
          <div>
            <CardTitle className="flex items-center gap-2">
              <ShieldQuestion className="h-[17px] w-[17px] text-warn" />
              待認領裝置（{rows.length}）
            </CardTitle>
            <CardDescription>
              新連上的裝置與解除配對的裝置都在這裡，認領前它們回報的事件與檔案一律不收
            </CardDescription>
          </div>
        </CardHeader>

        <div className="overflow-x-auto max-[720px]:overflow-visible">
          <table className="data-table w-full border-collapse">
            <thead>
              <tr>
                <th>裝置編號</th>
                <th>識別資訊</th>
                <th>狀態</th>
                <th>時間</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((d) => {
                const purging = d.purge_state === 'queued' || d.purge_state === 'running';
                return (
                <tr key={d.device_id}>
                  {/* 窄螢幕的 td 是兩欄 grid，內容要包成一個子元素，否則第二行會掉到標籤欄 */}
                  <td data-label="裝置編號">
                    <span className="block min-w-0">
                    <span className="block font-mono font-semibold">{d.device_id}</span>
                    {d.unpaired_at && d.label && (
                      <span className="block text-[12px]">{d.label}</span>
                    )}
                    {d.firmware_version && (
                      <span className="block font-mono text-[11px] text-muted-foreground">
                        韌體 {d.firmware_version}
                      </span>
                    )}
                    {d.unpaired_at && (
                      <span className="mt-1 flex flex-wrap items-center gap-1.5">
                        <Badge variant="info">已解除配對</Badge>
                        <span className="text-[11.5px] text-muted-foreground">
                          保留 {d.event_count ?? 0} 筆事件
                        </span>
                      </span>
                    )}
                    </span>
                  </td>
                  <td data-label="識別資訊" className="font-mono text-[11.5px]">
                    {/* MAC 要跟機殼標籤對得上才認領，這是唯一的實體驗證 */}
                    <ClaimHint hint={d.claim_hint} />
                  </td>
                  <td data-label="狀態">
                    <span className="block min-w-0">
                    <span className="flex flex-wrap items-center gap-1.5">
                      <Badge variant={d.online ? 'success' : 'muted'}>
                        <span className={`h-1.5 w-1.5 rounded-full ${
                          d.online ? 'status-pulse bg-success' : 'bg-muted-foreground'
                        }`} />
                        {d.online ? '線上' : '離線'}
                      </Badge>
                      {purging && (
                        <Badge variant="warn">
                          <Loader2 className="h-3 w-3 animate-spin" />資料刪除中
                        </Badge>
                      )}
                      {d.purge_state === 'failed' && <Badge variant="danger">刪除未完成</Badge>}
                    </span>
                    {d.purge_state === 'failed' && d.purge_error && (
                      <span className="mt-1 block break-words text-[11.5px] text-destructive">{d.purge_error}</span>
                    )}
                    </span>
                  </td>
                  <td data-label="時間" className="text-[11.5px] text-muted-foreground">
                    <span className="block">
                      <span className="block">{d.unpaired_at ? '解除配對' : '首次出現'}</span>
                      <span className="block font-mono">{fmtTime(d.unpaired_at ?? d.first_seen_at)}</span>
                    </span>
                  </td>
                  <td>
                    <div className="flex flex-wrap justify-end gap-1.5">
                      <Button size="sm" disabled={purging}
                              title={purging ? '資料刪除完成後才能重新認領' : undefined}
                              onClick={() => setTarget(d)}>
                        {d.unpaired_at ? '重新認領' : '認領'}
                      </Button>
                      {d.unpaired_at || d.purge_state ? (
                        <Button size="sm" variant="secondary" onClick={() => setRemoveKey(d.device_id)}>
                          刪除…
                        </Button>
                      ) : (
                        <Button size="sm" variant="secondary"
                                disabled={reject.isPending}
                                onClick={() => {
                                  if (confirm(
                                    `拒絕 ${d.device_id}？\n\n`
                                    + '它之後回報的資料都不會被接受，也不會再出現在這份清單。',
                                  )) reject.mutate(d.device_id);
                                }}>
                          拒絕
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      <Drawer
        open={target !== null}
        onOpenChange={(v) => !v && setTarget(null)}
        title={`認領 ${target?.device_id ?? ''}`}
      >
        {target && (
          <ClaimForm
            device={target}
            onDone={() => {
              setTarget(null);
              void qc.invalidateQueries({ queryKey: ['devices-pending'] });
              void qc.invalidateQueries({ queryKey: ['devices'] });
            }}
          />
        )}
      </Drawer>

      <Drawer
        open={removeKey !== null}
        onOpenChange={(v) => !v && setRemoveKey(null)}
        title={`刪除 ${removeKey ?? ''} 的資料`}
      >
        {removeKey && <DeviceRemoval deviceKey={removeKey} onDone={() => setRemoveKey(null)} />}
      </Drawer>
    </>
  );
}

/** 把裝置自報的 hint 攤成可讀的幾行；沒有就顯示破折號。 */
function ClaimHint({ hint }: { hint?: Record<string, unknown> }) {
  const entries = Object.entries(hint ?? {}).filter(([, v]) =>
    v !== null && v !== undefined && v !== '');
  if (entries.length === 0) {
    return <span className="text-muted-foreground">—</span>;
  }
  // 手機版表格轉卡片後，td 是 grid 容器，裸的 block span 會各自成為
  // grid item 而跳出值欄位；包一層把它們綁在一起。長 MAC/型號用
  // break-all 才不會撐破窄螢幕。
  return (
    <span className="block min-w-0 break-all">
      {entries.map(([k, v]) => (
        <span key={k} className="block">
          <span className="text-muted-foreground">{k}</span>{' '}
          {String(v)}
        </span>
      ))}
    </span>
  );
}

function ClaimForm({ device, onDone }: { device: PendingDevice; onDone: () => void }) {
  const [err, setErr] = useState('');

  const { data: sites } = useQuery({
    queryKey: ['sites'],
    queryFn: () => apiGet<Site[]>('/api/v1/sites'),
  });

  const mut = useMutation({
    mutationFn: (v: { label: string; site_code: string }) =>
      apiPost(`/api/v1/devices/${encodeURIComponent(device.device_id)}/claim`, v),
    onSuccess: onDone,
    onError: (e) => setErr(e instanceof Error ? e.message : '認領失敗'),
  });

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setErr('');
    const fd = new FormData(e.currentTarget);
    mut.mutate({
      label: String(fd.get('label') ?? ''),
      site_code: String(fd.get('site_code') ?? ''),
    });
  }

  return (
    <form onSubmit={onSubmit} className="space-y-5">
      <div className="rounded-sm border border-warn bg-warn-soft px-3.5 py-3 text-[12.5px]">
        認領前請確認下方識別資訊與機殼標籤相符。認領後這台裝置回報的事件與媒體就會被平台接受並保存。
        {device.unpaired_at && (
          <>這台裝置在 {fmtTime(device.unpaired_at)} 解除配對，平台保留的 {device.event_count ?? 0} 筆事件重新認領後會恢復可查。</>
        )}
      </div>

      <dl className="grid grid-cols-[84px_minmax(0,1fr)] gap-y-2 text-[12.5px]">
        <dt className="text-muted-foreground">裝置編號</dt>
        <dd className="font-mono">{device.device_id}</dd>
        <dt className="text-muted-foreground">識別資訊</dt>
        <dd className="font-mono"><ClaimHint hint={device.claim_hint} /></dd>
        <dt className="text-muted-foreground">首次出現</dt>
        <dd className="font-mono">{fmtTime(device.first_seen_at)}</dd>
      </dl>

      <label className="block">
        <span className="mb-2 block text-[12.5px] font-semibold">顯示名稱</span>
        <Input name="label" maxLength={80} placeholder={device.device_id}
               defaultValue="" />
        <span className="mt-1.5 block text-[11.5px] text-muted-foreground">
          留空則沿用裝置編號
        </span>
      </label>

      <label className="block">
        <span className="mb-2 block text-[12.5px] font-semibold">所屬場域</span>
        <select name="site_code" required
                className="min-h-[44px] w-full rounded-sm border border-border bg-card px-3 text-sm">
          <option value="">請選擇…</option>
          {(sites ?? []).map((s) => (
            <option key={s.code} value={s.code}>{s.code}　{s.name}</option>
          ))}
        </select>
        <span className="mt-1.5 block text-[11.5px] text-muted-foreground">
          事件要有場域歸屬才查得到，認領時就指派好
        </span>
      </label>

      {err && <p className="text-[12.5px] font-semibold text-destructive">{err}</p>}

      <Button type="submit" disabled={mut.isPending}>
        {mut.isPending ? (
          <><Loader2 className="h-[14px] w-[14px] animate-spin" />認領中…</>
        ) : '確認認領'}
      </Button>
    </form>
  );
}
